/**
 * SitePick front — エントリ
 *
 * Vanilla ES6+ / modules。jQuery 不使用。
 * window.SITEPICK は wp_localize_script から供給される
 * （restUrl / nonce / demoOrigin / currency / limits / urls / messages）。
 *
 * Phase 1: 共通 UI（reveal / accordion）
 * Phase 3: ファセット
 * Phase 4: お気に入り / 比較（storage → store → toggle 同期 → トレイ → 各ページ）
 */

import { initReveal } from "./reveal.js";
import { initNav } from "./nav.js";
import { initAccordion } from "./accordion.js";
import { initFacet } from "./facet.js";
import { initCatalogView } from "./catalog-view.js";
import { initLiveRegion } from "./live-region.js";
import { initToggleButtons } from "./toggle-buttons.js";
import { initCompareTray } from "./compare-tray.js";
import { initFavoritesPage } from "./favorites.js";
import { initComparePage } from "./compare.js";
import { initListTracking } from "./list-tracking.js";
import { initDetailTracking } from "./detail-tracking.js";
import { initPreview } from "./preview.js";
import { initSiteDevices } from "./site-devices.js";
import { initCheckout } from "./checkout.js";
import { initCoupon } from "./coupon.js";
import { initPurchaseComplete } from "./purchase-complete.js";
import { initOnboarding } from "./onboarding.js";
import { initRequirementsSetup } from "./requirements-setup.js";
import { initContact } from "./contact.js";
import { initChat } from "./chat.js";
import { initAsk } from "./ask.js";
import { initStickyCta, initAskOpenTracking } from "./sticky-cta.js";
import { initEngagementSync } from "./engagement-sync.js";
import { initHold } from "./hold.js";
import { initHoldPage } from "./hold-page.js";
import { initShare } from "./share.js";
import { initLiveFullscreen } from "./live-fullscreen.js";
import { initLiveColors } from "./live-colors.js";
import { initLiveCustomize } from "./live-customize.js";
import { initSiteColors } from "./site-colors.js";
import { initSaleCountdown } from "./sale-countdown.js";
import { initDiagnosis } from "./diagnosis.js";
import { initInvoice } from "./invoice.js";
import { initRecentSites } from "./recent-sites.js";

/**
 * 各モジュールを独立に起動する。1 つが例外を投げても他は動かす
 * （＝ JS エラーで主要コンテンツやナビが巻き添えで死なない）。
 *
 * @param {string} name ログ用の名前。
 * @param {() => void} fn 初期化関数。
 */
function run(name, fn) {
  try {
    fn();
  } catch (error) {
    console.error(`[sitepick] ${name} init failed`, error);
  }
}

function boot() {
  run("reveal", () => initReveal(document));
  run("saleCountdown", () => initSaleCountdown(document));
  run("nav", () => initNav(document));
  run("accordion", () => initAccordion(document));
  run("facet", () => initFacet(document));
  run("catalogView", () => initCatalogView(document));

  // Phase 4
  run("liveRegion", () => initLiveRegion());
  run("toggleButtons", () => initToggleButtons(document));
  run("compareTray", () => initCompareTray());
  run("favoritesPage", () => initFavoritesPage());
  run("comparePage", () => initComparePage());
  run("listTracking", () => initListTracking());
  run("detailTracking", () => initDetailTracking());

  // Phase 5
  run("preview", () => initPreview());
  run("siteDevices", () => initSiteDevices());

  // Phase 6
  run("checkout", () => initCheckout());
  run("coupon", () => initCoupon());
  run("purchaseComplete", () => initPurchaseComplete());
  run("onboarding", () => initOnboarding());
  run("requirementsSetup", () => initRequirementsSetup());

  // Phase 8
  run("contact", () => initContact());
  run("chat", () => initChat());

  // 相談チャット（custom-chat spec §9）: 会話画面 / SP 追従バー / 「相談する」の計測。
  run("ask", () => initAsk());
  run("stickyCta", () => initStickyCta());
  run("askOpen", () => initAskOpenTracking());

  // お気に入り / 比較の内部集計（docs/06 §9）。store の初期化後に起動する。
  run("engagementSync", () => initEngagementSync());

  // site-share（共有 / 実物ビューアの全画面）
  run("share", () => initShare(document));
  run("liveFullscreen", () => initLiveFullscreen(document));
  // 実物確認画面のカラー切替（docs/07 §13）。色の案を用意した商品だけボタンが出る。
  run("liveColors", () => initLiveColors(document));
  // 実物確認画面の「自社の情報で見る」（docs/22）。パネルを出力した商品だけで動く（要素が無ければ何もしない）。
  run("liveCustomize", () => initLiveCustomize(document));
  // 一覧カード / 商品詳細のカラー切替（parts/color-pick.php）。
  run("siteColors", () => initSiteColors());
  // かんたん診断（販売導線 v2 Phase 2）: 設問のステップ表示と計測。
  run("diagnosis", () => initDiagnosis());
  // 請求書払いの申請（販売導線 v2 Phase 3）: view_invoice_form / request_invoice。
  run("invoice", () => initInvoice());
  // 「前回見たサイト」（docs/06 §10）。商品詳細・実物で記録し、2 回目以降の訪問でトップと一覧に出す。
  run("recentSites", () => initRecentSites());
  // 24 時間取置き / 再販通知（reservation-notify）。商品詳細・実物確認の導線と holder 表示、取置き / 通知ページ。
  // 購入ブロック「すぐに決められない方へ」（オリジナル制作・取置き）の開閉。既定は閉じる。
  run("hold", () => initHold());
  run("holdPage", () => initHoldPage());
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot, { once: true });
} else {
  boot();
}
